import { Button, Input, Modal, Select } from '@bennows/boows-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFailure } from '../../lib/api';
import { useToast } from '../../lib/toasts';
import { operatorFailureKey } from './failureKey';
import { roleOptions } from './roleOptions';
import { useOperatorWrites, type OperatorRole } from './useOperators';

const FORM_ID = 'invite-operator';

/** Creates an operator and mails the set-password link (REQ-140-002). */
export function InviteOperatorDialog({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { create } = useOperatorWrites();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OperatorRole>('viewer');
  const [missing, setMissing] = useState({ name: false, email: false });

  const close = () => {
    setName('');
    setEmail('');
    setRole('viewer');
    setMissing({ name: false, email: false });
    create.reset();
    onClose();
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const empty = { name: name.trim() === '', email: email.trim() === '' };
    setMissing(empty);
    if (empty.name || empty.email) return;
    create.mutate(
      { display_name: name.trim(), email: email.trim(), role },
      {
        onSuccess: (operator) => {
          toast(t('operators.inviteForm.sent', { email: operator.email }));
          close();
        },
      },
    );
  };
  const invalidEmail = create.error instanceof ApiFailure && create.error.slug === 'validation-failed';
  const message = create.error && !invalidEmail ? operatorFailureKey(create.error, 'operators.inviteForm.mailFailed') : null;

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('operators.inviteForm.title')}
      description={t('operators.inviteForm.description')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} loading={create.isPending}>
            {t('operators.inviteForm.submit')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="ui-console-form" onSubmit={onSubmit} noValidate>
        <Input
          label={t('operators.inviteForm.name')}
          value={name}
          onChange={setName}
          maxLength={120}
          required
          invalid={missing.name}
          help={missing.name ? t('signIn.required') : undefined}
        />
        <Input
          label={t('operators.inviteForm.email')}
          type="email"
          value={email}
          onChange={setEmail}
          required
          invalid={missing.email || invalidEmail}
          help={missing.email ? t('signIn.required') : invalidEmail ? t('signIn.invalidEmail') : undefined}
        />
        <Select label={t('operators.inviteForm.role')} options={roleOptions(t)} value={role} onChange={(id) => setRole(id as OperatorRole)} />
        {message ? (
          <p className="ui-auth__message" role="alert">
            {t(message)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
